import React, { useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import { Field, reduxForm, reset } from 'redux-form';
import moment from 'moment';

import ControllingPersonTypes from 'common/Accounts/ControllingPersonTypes';

import { addControllingPerson } from 'actions';
import usePrevious from 'hooks/usePrevious';

const AddAccountControllingPersonForm = ({
  dispatch,
  title,
  sourceAccountId,
  forms,
  accounts,
  isLinkingControllingPerson,
  isLinkingControllingPersonComplete,
  onSuccess,
  accountIsActive,
  rc518CpForms
}) => {
  const [entityFormId, setEntityFormId] = useState(null);
  const [cpFormId, setCpFormId] = useState(null);
  const [cpType, setCpType] = useState(null);
  const [cpSelectionText, setCpSelectionText] = useState(null);

  const validCrsEntityFormTypes = [5, 22, 24];
  const validCrsCpFormTypes = [7, 21, 23];

  const validRcEntityFormTypes = [14, 39];
  const validRcCpFormTypes = [15, 40];

  const [entityFormTypes, setAvaliableEntityFormTypes] = useState({
    crs: validCrsEntityFormTypes,
    rc: validRcEntityFormTypes
  });
  const [cpFormTypes, setAvaliableCpFormTypes] = useState({
    crs: validCrsCpFormTypes,
    rc: validRcCpFormTypes
  });

  const previsLinkingControllingPerson = usePrevious(isLinkingControllingPerson);

  const clearForm = () => {
    setEntityFormId(null);
    setCpFormId(null);
    setCpType(null);
    setAvaliableCpFormTypes({ crs: validCrsCpFormTypes, rc: validRcCpFormTypes });
    setAvaliableEntityFormTypes({ crs: validCrsEntityFormTypes, rc: validRcEntityFormTypes });
  };

  useEffect(() => {
    if (previsLinkingControllingPerson && !isLinkingControllingPerson && isLinkingControllingPersonComplete) {
      clearForm();
      dispatch(reset('add-controlling-person-form'));
    }
  }, [isLinkingControllingPerson, isLinkingControllingPersonComplete]);

  const formDescription = (form) => {
    let expirationText = '';

    if (form.validUntil && form.validUntil !== null) {
      const expiryReason = form.expiryReasonDescription !== null ? `(${ form.expiryReasonDescription })` : '';
      if (form.validUntil.includes('9999')) {
        expirationText = `Evergreen ${ expiryReason }`;
      } else {
        expirationText = `${ moment(form.validUntil).format('MMM DD, YYYY') } ${ expiryReason }`;
      }
    }

    if (sourceAccountId) {
      return `${ form.formTypeDescription } - ${ moment(form.dateSigned).format('MMM DD, YYYY') } - ${ form.entityName ?? 'N/A' } - ${ expirationText }`;
    }
    return `${ form.formTypeDescription } - ${ moment(form.dateSigned).format('MMM DD, YYYY') } - ${ form.accountNumber ?? 'N/A' } - ${ expirationText }`;
  };

  const onFormSubmit = (e) => {
    e.preventDefault();
    if (entityFormId && cpFormId && sourceAccountId) {
      dispatch(addControllingPerson(sourceAccountId, entityFormId, cpFormId, cpType));
    }
  };

  const updateCpSelectionText = () => {
    const rcOnlyText = 'Select the corresponding RC form.';
    const crsOnlyText = 'Select the corresponding CRS form.';

    if (entityFormTypes.crs.length === 0 || cpFormTypes.crs.length === 0) {
      setCpSelectionText(rcOnlyText);
      return;
    }

    if (entityFormTypes.rc.length === 0 || cpFormTypes.rc.length === 0) {
      setCpSelectionText(crsOnlyText);
      return;
    }

    setCpSelectionText(null);
  };

  useEffect(() => {
    updateCpSelectionText();
  }, [entityFormTypes, cpFormTypes]);

  const handleEntityFormChange = (selectedValue) => {
    if (!selectedValue || selectedValue.trim() === '') {
      setEntityFormId(null);
      setAvaliableCpFormTypes({ crs: validCrsCpFormTypes, rc: validRcCpFormTypes });
      return;
    }

    const { id, formType } = JSON.parse(selectedValue);

    if (entityFormTypes.crs.includes(formType)) {
      setAvaliableCpFormTypes({ crs: validCrsCpFormTypes, rc: [] });
    } else if (entityFormTypes.rc.includes(formType)) {
      setAvaliableCpFormTypes({ crs: [], rc: validRcCpFormTypes });
    } else {
      setAvaliableCpFormTypes({ crs: validCrsCpFormTypes, rc: validRcCpFormTypes });
    }

    setEntityFormId(id);
  };

  const handleCpFormChange = (selectedValue) => {
    if (!selectedValue || selectedValue.trim() === '') {
      setCpFormId(null);
      setAvaliableEntityFormTypes({ crs: validCrsEntityFormTypes, rc: validRcEntityFormTypes });
      return;
    }

    const { id, formType } = JSON.parse(selectedValue);

    if (cpFormTypes.crs.includes(formType)) {
      setAvaliableEntityFormTypes({ crs: validCrsEntityFormTypes, rc: [] });
    } else if (cpFormTypes.rc.includes(formType)) {
      setAvaliableEntityFormTypes({ crs: [], rc: validRcEntityFormTypes });
    } else {
      setAvaliableEntityFormTypes({ crs: validCrsEntityFormTypes, rc: validRcEntityFormTypes });
    }

    setCpFormId(id);
  };

  return (<div className="add-controlling-person-form">
    { title && <h2 className="columns"><strong>{ title }</strong></h2> }
    <form onSubmit={ onFormSubmit }>
      <div className="form-group columns">
        <div className="col-2">
          <label htmlFor="entity-form-id" className="panel-body-label">Entity Form*</label>
        </div>
        <div className="col-4">
          <Field
            id="entity-form-id"
            name="entityFormId"
            component="select"
            className="form-select"
            required
            value={ entityFormId ? entityFormId.toString() : null }
            onChange={ e => handleEntityFormChange(e.target.value) }
            disabled={ !accountIsActive }
            data-testid="entity-form-cp-manual-link"
          >
            <option value="">Select Entity form...</option>
            {forms && forms.filter(form => ([entityFormTypes.crs, entityFormTypes.rc].some(formTypes => formTypes.includes(form.formTypeEnum)))
              && form.workflowStage === 100 && form.isValid).map(f => (<option key={ f.id } value={ JSON.stringify({ id: f.id, formType: f.formTypeEnum }) }>{ formDescription(f) }</option>)) }
          </Field>
        </div>
        {cpSelectionText && <div className="col-6">
          <p className="cp-selection-text" data-testid="cp-selection-text">
            {cpSelectionText}
          </p>
          </div>}
      </div>
      <div className="form-group columns">
        <div className="col-2">
          <label htmlFor="cp-form-id" className="panel-body-label">CP Form*</label>
        </div>
        <div className="col-4">
          <Field
            id="cp-form-id"
            name="cpFormId"
            component="select"
            className="form-select"
            required
            value={ cpFormId ? cpFormId.toString() : null }
            onChange={ e => handleCpFormChange(e.target.value) }
            disabled={ !accountIsActive }
            data-testid="cp-form-cp-manual-link"
          >
            <option value="">Select CP form...</option>
            {forms && forms.filter(form => (cpFormTypes.crs.includes(form.formTypeEnum)
              || (cpFormTypes.rc.includes(form.formTypeEnum) && rc518CpForms.includes(form.id)))
              && form.workflowStage === 100 && form.isValid).map(f => (<option key={ f.id } value={ JSON.stringify({ id: f.id, formType: f.formTypeEnum }) }>{ formDescription(f) }</option>)) }
          </Field>
        </div>
      </div>
      <div className="form-group columns">
        <div className="col-2">
          <label htmlFor="cp-form-id" className="panel-body-label">CP Type</label>
        </div>
        <div className="col-4">
          <Field
            id="cp-type"
            name="cpType"
            component="select"
            className="form-select"
            value={ cpType }
            onChange={ e => setCpType(e.target.value) }
            disabled={ !accountIsActive }
            data-testid="cp-type-cp-manual-link"
          >
            <option value="">Select CP Type...</option>
            { ControllingPersonTypes.map(x => (
              <option key={ x.key } value={ x.key }> { x.value } </option>
            )) }
          </Field>
        </div>
      </div>
      <div className="form-group columns">
        <div className="col-2" />
        <div className="col-6">
          <button
            id="add-cp-submit-btn"
            type="submit"
            className="btn btn-primary"
            disabled={ !accountIsActive || isLinkingControllingPerson || (!entityFormId || !cpFormId) }
          >
            { isLinkingControllingPerson && <span className="icon loading mr" /> }
            Link Controlling Person
          </button>
        </div>
      </div>
    </form>
  </div>);
};

AddAccountControllingPersonForm.propTypes = {
  dispatch: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  sourceAccountId: PropTypes.number,
  forms: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  accounts: PropTypes.arrayOf(PropTypes.shape()),
  isLinkingControllingPerson: PropTypes.bool.isRequired,
  isLinkingControllingPersonComplete: PropTypes.bool.isRequired,
  onSuccess: PropTypes.func,
  accountIsActive: PropTypes.bool.isRequired,
  rc518CpForms: PropTypes.arrayOf(PropTypes.number)
};

AddAccountControllingPersonForm.defaultProps = {
  sourceAccountId: null,
  accounts: null,
  onSuccess: null,
  rc518CpForms: []
};

const AddAccountControllingPersonReduxForm = reduxForm({
  form: 'add-controlling-person-form'
})(AddAccountControllingPersonForm);

const mapDispatchToProps = dispatch => ({ dispatch });

function mapStateToProps(state) {
  const {
    account: {
      isLinkingControllingPerson,
      isLinkingControllingPersonComplete,
      storeAccount
    }
  } = state;

  return {
    isLinkingControllingPerson,
    isLinkingControllingPersonComplete,
    rc518CpForms: storeAccount?.rc518CpForms ?? []
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AddAccountControllingPersonReduxForm);
